import { useState } from 'react';
import { VideoStreamRenderer } from '@azure/communication-calling';

const useScreenSharing = ({
  call,
  onScreenShareStateChange,
  acsRoomId,
  userId,
  socket,
  renderScreenAfterScreenSharingStop,
  clientObjectArr = [],
  isUserCallCreator = false,
  displayName = ''
}) => {
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [pendingScreenShareRequest, setPendingScreenShareRequest] = useState(null);

  // Check if someone is already screen sharing
  const isSomeoneScreenSharing = () => {
    const someoneSharing = clientObjectArr.some(participant => participant.isScreenSharing);
    return someoneSharing;
  };

  // Get current screen sharer
  const getCurrentScreenSharer = () => {
    return clientObjectArr.find(participant => participant.isScreenSharing);
  };

  // Request screen share permission from admins
  const requestScreenSharePermission = () => {
    const currentScreenSharer = getCurrentScreenSharer();
    const requestId = Date.now().toString();

    // Check if current user is an admin
    const currentUser = clientObjectArr.find(participant => participant.userId === userId);
    const isCurrentUserAdmin = currentUser && currentUser.userType === 'Admin';

    // If current user is admin, they don't need permission - force stop current screen share and start their own
    if (isCurrentUserAdmin) {
      if (currentScreenSharer) {
        // Force stop current screen share
        socket.emit('BE-update-status', {
          acsRoomId,
          senderUserId: userId,
          socketId: socket.id,
          data: {
            currentScreenSharerId: currentScreenSharer.userId,
            adminOverride: true
          },
          type: 'ForceStopScreenShare'
        });
      }
      // Start screen sharing immediately
      startScreenSharing();
      return;
    }

    // Find all admins in the room
    const admins = clientObjectArr.filter(participant =>
      participant.userType === 'Admin' && participant.userId !== userId
    );

    if (admins.length === 0) {
      // No admins to ask, proceed with screen sharing
      startScreenSharing();
      return;
    }

    // Send permission request to all admins
    admins.forEach(admin => {
      socket.emit('BE-update-status', {
        acsRoomId,
        senderUserId: userId,
        socketId: socket.id,
        data: {
          requestId,
          requestingUser: {
            userId,
            displayName,
            socketId: socket.id
          },
          currentScreenSharer: currentScreenSharer ? {
            userId: currentScreenSharer.userId,
            displayName: currentScreenSharer.displayName
          } : null
        },
        type: 'ScreenSharePermissionRequest',
        adminId: admin.userId
      });
    });

    setPendingScreenShareRequest({
      requestId,
      timestamp: Date.now()
    });

    // Set timeout to clear pending request after 30 seconds
    setTimeout(() => {
      setPendingScreenShareRequest(prev => {
        if (prev && prev.requestId === requestId) {
          // Emit timeout notification
          socket.emit('BE-update-status', {
            acsRoomId,
            senderUserId: userId,
            socketId: socket.id,
            data: {
              requestId,
              timeout: true
            },
            type: 'ScreenShareRequestTimeout'
          });
          return null;
        }
        return prev;
      });
    }, 30000); // 30 seconds timeout
  };

  // Start screen sharing
  const startScreenSharing = async () => {
    try {
      if (call) {
        await call.startScreenSharing();
        const screenStream = call.localVideoStreams.find(
          (stream) => stream.mediaStreamType === 'ScreenSharing'
        );

        if (screenStream) {
          setIsScreenSharing(true);
          // Clear any pending request
          setPendingScreenShareRequest(null);
        }
        socket.emit('BE-update-status', {
          acsRoomId: acsRoomId,
          senderUserId: userId,
          socketId: socket.id,
          type: 'RenderScreenShare'
        });
      }
    } catch (error) {
      console.error('Error starting screen sharing:', error);
      setPendingScreenShareRequest(null);
    }
  };

  // Stop screen sharing
  const stopScreenSharing = async () => {
    try {
      if (call) {
        await call.stopScreenSharing();
        socket.emit('BE-update-status', {
          acsRoomId: acsRoomId,
          senderUserId: userId,
          socketId: socket.id,
          type: 'StopScreenShare'
        });
        renderScreenAfterScreenSharingStop();
        setIsScreenSharing(false);
      }
    } catch (error) {
      console.error('Error stopping screen sharing:', error);
    }
  };

  // Handle screen share permission request (for admins)
  const handleScreenSharePermissionRequest = (requestData) => {
    // This will be called from the main component when an admin receives a request
    return {
      requestId: requestData.requestId,
      requestingUser: requestData.requestingUser,
      currentScreenSharer: requestData.currentScreenSharer
    };
  };

  // Handle screen share permission response (for requesting user)
  const handleScreenSharePermissionResponse = (response) => {
    if (pendingScreenShareRequest && pendingScreenShareRequest.requestId === response.requestId) {
      setPendingScreenShareRequest(null);

      if (response.approved) {
        // Add a small delay to ensure any current screen share is fully stopped
        setTimeout(() => {
          startScreenSharing();
        }, 300);
      }
      // If denied, the user will get a toast notification from the main component
    }
  };

  // Force stop screen sharing (when admin approves new request)
  const forceStopScreenSharing = async () => {
    try {
      if (call && isScreenSharing) {
        await call.stopScreenSharing();
        socket.emit('BE-update-status', {
          acsRoomId: acsRoomId,
          senderUserId: userId,
          socketId: socket.id,
          type: 'StopScreenShare'
        });
        renderScreenAfterScreenSharingStop();
        setIsScreenSharing(false);
      }
    } catch (error) {
      console.error('Error force stopping screen sharing:', error);
    }
  };

  return {
    isScreenSharing,
    setIsScreenSharing,
    startScreenSharing,
    stopScreenSharing,
    renderScreenAfterScreenSharingStop,
    isSomeoneScreenSharing,
    getCurrentScreenSharer,
    requestScreenSharePermission,
    handleScreenSharePermissionRequest,
    handleScreenSharePermissionResponse,
    forceStopScreenSharing,
    pendingScreenShareRequest
  };
};

export default useScreenSharing;